import React, { useEffect, useState } from "react";
import { Header, Section } from "../components/layout";
import { useParams } from "react-router-dom";
import { BaseUrl, mealUrl, media } from "../server";
import { Loader } from "../components/utils";
import { useStateContext } from "../context/ContextProvider";
import { Fotter } from "../components";
import { useTranslation } from "react-i18next";
import RelatedBigMeals from "../components/RelatedBigMeals";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { FaRegEye, AiOutlineLike, AiFillLike, FiShare2, FiChevronLeft, FiChevronRight, FiHeart, FiClock, FiStar, FiMapPin } from "../components/utils/icons";
import { useDispatch } from "react-redux";
import { getMeal } from "../redux/actions/meals";
import axios from "axios";
import { useSelector } from "react-redux";
import ShareMeal from "../components/utils/ShareMeal";
import { motion, AnimatePresence } from "framer-motion";

function MealDetails() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { currentDir } = useStateContext();
  const [like, setLike] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getMeal(id));
    window.scrollTo(0, 0);
  }, [id, dispatch]);

  const { mealData, isLoadingMeal } = useSelector((state) => state.Meals);
  const [likes, setLikes] = useState(mealData?.likes || 0);

  const LikeHandler = async () => {
    if (!like) {
      setLikes(likes + 1);
      try {
        const { data } = await axios.get(`${BaseUrl}/blogs/like/${id}`);
        if (data.success) {
          setLike(true);
        }
      } catch (error) {
        console.log(error.response?.data?.message);
        setLikes(likes);
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  const imageVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 20, delay: 0.2 },
    },
  };

  const titleVariants = {
    hidden: { x: currentDir === "ltr" ? -50 : 50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15, delay: 0.3 },
    },
  };

  const getFormattedDate = () => {
    if (!mealData?.createdAt) return "";
    const date = new Date(mealData.createdAt);
    return date.toLocaleDateString(currentDir === "ltr" ? "en-US" : "ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Header active={4} DropActive={1} />
      {isLoadingMeal ? (
        <Loader />
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="bg-gradient-to-b from-gray-50 to-white dark:from-n-9 dark:to-n-8"
        >
          {/* Hero Section with Parallax Effect */}
          <div className="relative h-[40vh] md:h-[50vh] lg:h-[60vh] overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            />
            <motion.img
              src={`${media}${mealData?.Image}`}
              alt={currentDir === "ltr" ? mealData?.titleEn : mealData?.titleAr}
              className="w-full h-full object-cover"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8 }}
              onLoad={() => setImageLoaded(true)}
            />
            <motion.div
              className="absolute bottom-0 left-0 right-0 z-20 pb-12 px-4 md:px-8 lg:px-16"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <div className="container mx-auto">
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 font-kufi py-1 bg-color-1/90 backdrop-blur-sm text-white text-sm rounded-full">
                    {mealData?.categoryId?.titleAr || t("featured")}
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-kufi font-bold text-white mb-4 max-w-4xl">
                  {currentDir === "ltr" ? mealData?.titleEn : mealData?.titleAr}
                </h1>
                <p className="text-white/90 font-kufi text-base md:text-lg max-w-2xl">
                  {currentDir === "ltr" ? mealData?.textEn?.substring(0, 120) : mealData?.textAr?.substring(0, 120)}...
                </p>
              </div>
            </motion.div>
          </div>

          <Section>
            <div className="container mx-auto px-4 py-12 lg:py-16">
              <div className="max-w-6xl mx-auto">
                {/* Breadcrumb */}
                <motion.div
                  variants={itemVariants}
                  className="flex items-center gap-2 text-sm text-n-5 dark:text-n-4 mb-8"
                >
                  <Link to="/" className="hover:text-color-1 font-kufi transition-colors">
                    {t("home")}
                  </Link>
                  <FiChevronRight size={14} />
                  <Link to="/AllResturentMeals" className="hover:text-color-1 font-kufi transition-colors">
                    {t("all meals")}
                  </Link>
                  <FiChevronRight size={14} />
                  <span className="text-color-1 font-kufi">
                    {currentDir === "ltr" ? mealData?.titleEn : mealData?.titleAr}
                  </span>
                </motion.div>

                <div className="flex flex-col lg:flex-row gap-12">
                  {/* Main Content */}
                  <motion.div variants={itemVariants} className="flex-1">
                    {/* Stats Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white dark:bg-n-8 rounded-2xl shadow-lg mb-8">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-color-1/10 rounded-full">
                            <FaRegEye className="text-color-1" size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-n-5 font-kufi dark:text-n-4">{t("views")}</p>
                            <p className="font-bold text-n-7 dark:text-n-2">{mealData?.views || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-color-1/10 rounded-full">
                            {like ? (
                              <AiFillLike className="text-color-1" size={18} />
                            ) : (
                              <AiOutlineLike className="text-color-1" size={18} />
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-n-5 font-kufi dark:text-n-4">{t("likes")}</p>
                            <p className="font-bold text-n-7 dark:text-n-2">{likes}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-color-1/10 rounded-full">
                            <FiClock className="text-color-1" size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-n-5 font-kufi dark:text-n-4">{t("Preparation time")}</p>
                            <p className="font-bold text-n-7 dark:text-n-2 text-sm">
                              {mealData?.preparationTime ? `${mealData.preparationTime} ${" "} ${t("minutes")}` : "15-20" + " " +t("minutes")}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={LikeHandler}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                            like
                              ? "bg-color-1 text-white"
                              : "bg-gray-100 dark:bg-n-7 text-n-7 dark:text-n-2 hover:bg-color-1/10"
                          }`}
                        >
                          {like ? <AiFillLike /> : <AiOutlineLike />}
                          <span>{like ? t("liked") : t("like")}</span>
                        </motion.button>
                        <ShareMeal
                          url={`${mealUrl}${mealData?._id}`}
                          title={mealData?.titleAr}
                          description={
                            currentDir === "ltr" ? mealData?.textEn : mealData?.textAr
                          }
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <motion.div variants={itemVariants} className="prose prose-lg dark:prose-invert max-w-none">
                      <h2 className="text-2xl font-kufi font-bold mb-4 text-n-7 dark:text-n-2">
                        {t("description")}
                      </h2>
                      <div
                        className="text-n-6 dark:text-n-3 font-kufi font-light leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            currentDir === "ltr" ? mealData?.textEn : mealData?.textAr
                          ),
                        }}
                      />
                    </motion.div>

                    {/* Additional Info Cards */}
                    {mealData?.ingredients && (
                      <motion.div variants={itemVariants} className="mt-12">
                        <h2 className="text-2xl font-bold mb-6 text-n-7 dark:text-n-2">
                          {t("ingredients")}
                        </h2>
                        <div className="flex flex-wrap gap-3">
                          {mealData.ingredients.map((ingredient, idx) => (
                            <motion.span
                              key={idx}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: idx * 0.05 }}
                              className="px-4 py-2 bg-gray-100 dark:bg-n-7 rounded-full text-sm text-n-7 dark:text-n-2"
                            >
                              {ingredient}
                            </motion.span>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Sidebar */}
                  <motion.div variants={itemVariants} className="lg:w-96">
                    <div className="sticky top-24">
                      {/* Price Card */}
                      {mealData?.price && (
                        <div className="bg-gradient-to-br from-color-1 to-color-2 rounded-2xl p-6 text-white mb-6 shadow-xl">
                          <p className="text-sm font-kufi font-light opacity-90 mb-2">{t("starting from")}</p>
                          <p className="text-4xl font-bold mb-2">
                            {mealData.price}
                            <span className="text-sm font-kufi font-light ml-3"> جنية</span>
                            <span className="text-lg font-kufi font-light"> / {t("meal")}</span>
                          </p>
                          <p className="text-sm font-kufi font-light opacity-90">{t("priceExcludesTax")}</p>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="mt-4 w-full bg-white font-kufi  text-color-1 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                          >
                            {t("order now")}
                          </motion.button>
                        </div>
                      )}

                      {/* Chef Info */}
                      {mealData?.chef && (
                        <div className="bg-white dark:bg-n-8 rounded-2xl p-6 shadow-lg mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-color-1 to-color-2 p-0.5">
                              <img
                                src={mealData.chef.avatar || "/default-avatar.png"}
                                alt={mealData.chef.name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="text-sm text-n-5 dark:text-n-4">{t("prepared by")}</p>
                              <p className="font-bold text-n-7 dark:text-n-2">{mealData.chef.name}</p>
                              <p className="text-sm text-color-1">{mealData.chef.specialty}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Nutrition Info */}
                      {mealData?.nutrition && (
                        <div className="bg-white dark:bg-n-8 rounded-2xl p-6 shadow-lg">
                          <h3 className="font-bold text-lg mb-4 text-n-7 dark:text-n-2">
                            {t("nutritional information")}
                          </h3>
                          <div className="space-y-3">
                            {Object.entries(mealData.nutrition).map(([key, value]) => (
                              <div key={key} className="flex justify-between items-center">
                                <span className="text-sm text-n-5 dark:text-n-4">
                                  {t(key)}
                                </span>
                                <span className="font-semibold text-n-7 dark:text-n-2">
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </Section>

          {/* Related Meals Section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <RelatedBigMeals currentMealId={id} />
          </motion.div>

          <Fotter />
        </motion.div>
      )}
    </>
  );
}

export default MealDetails;